import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserProfession } from '~/enums/UserProfession';
import { useAppSelector } from '~/hooks/useAppSelector';
import {
  FETCH_OPEN_JOBS_FOR_RECENT_PROFESSIONAL_ACTION,
  fetchOpenJobsForRecentProfessional,
  showMessage,
} from '../../../actions';
import { clearSuccess } from '../../../actions/success';
import { trackEvent } from '../../../api/analytics';
import { createSuccessSelector } from '../../../api/selectors';
import { DotsMenu, MenuItem } from '../../../commonComponents/DotsMenu';
import { IconButton } from '../../../commonComponents/IconButton';
import history from '../../../history';
import shareIcon from '../../../images/share-icon.svg';
import { Colors } from '../../../themes/colors';
import { IInviteButtonUser } from '../interfaces';
import { PosthogService, POSTHOG_EVENTS } from '../../../services/posthog';
import { getInviteLocationCategory, type InviteSource } from '../../../utils/posthog/inviteTracking';

const actions = [FETCH_OPEN_JOBS_FOR_RECENT_PROFESSIONAL_ACTION];
const successSelector = createSuccessSelector(actions);

// InviteButton is used in Professional Hub and Dashboard contexts
type InviteButtonSource = Extract<InviteSource,
  'favorites' | 'worked_with' | 'new_to_you' | 'dashboard' |
  'shifts_open' | 'shifts_action_needed' | 'shift_details' | 'schedule_list'>;

interface IInviteButtonProps {
  user: IInviteButtonUser;
  disabled?: boolean;
  listener?: () => void;
  showTooltip?: boolean;
  position?: 'left' | 'right';
  onValidate?: () => boolean;
  CustomMenuIcon?: ({ isHovered }: { isHovered: boolean }) => React.ReactNode;
  source?: InviteButtonSource;
}


export const InviteButton = ({
  user,
  disabled,
  listener,
  showTooltip = true,
  position = 'right',
  onValidate,
  CustomMenuIcon,
  source,
}: IInviteButtonProps) => {
  const dispatch = useDispatch();
  const isSuccessRequest = useSelector((state) => successSelector(state));
  const { offers, professionalId } = useAppSelector((state) => state.job.recentOpenJobs);
  const selectedOffice = useAppSelector(
    (state) => state.user.selectedChildOffice ?? state.user.user,
  );
  const allowedRequest = selectedOffice?.allowedRequest || {};
  const isDisabled = disabled || !(allowedRequest as any)?.tempOrMdbShifts;

  const handleInviteToExistingShift = () => {
    // Legacy tracking for backwards compatibility
    trackEvent('Professional Hub - Existing Shift Clicked', {
      professionalId: user?.id,
      profession: user?.profession,
    });

    // New PostHog tracking with single event and properties
    const posthog = PosthogService.getInstance();

    posthog.track(POSTHOG_EVENTS.OFFICE_INVITE_SENT, {
      source: source || 'unknown',
      invite_type: 'existing_shift',
      location_category: getInviteLocationCategory(source),
      professional_id: user?.id,
      profession: user?.profession,
      office_id: user?.officeId,
    });

    dispatch(
      fetchOpenJobsForRecentProfessional({
        officeId: user?.officeId,
        professionalId: user?.id,
        profession: user?.profession,
        page: 1,
      }),
    );
  };

  const renderCustomMenuIcon = ({ isHovered }: { isHovered: boolean }) => {
    return CustomMenuIcon ? (
      <CustomMenuIcon isHovered={isHovered} />
    ) : (
      <IconButton
        buttonStyle={{
          ...styles.buttonStyle,
          backgroundColor: isDisabled ? Colors.primary_200 : Colors.primary_500,
        }}
        imgStyle={styles.imgStyle}
        src={shareIcon}
        tooltipContent={showTooltip ? 'Invite' : ''}
        disabled={isDisabled}
      />
    );
  };

  const getOrderedProfessionNames = useCallback(() => {
    const userProfessionCodesArr = user?.professions ?? [];

    const professionCodesOrder = [
      UserProfession.DN,
      UserProfession.RDH,
      UserProfession.DA,
      UserProfession.FD,
    ];

    const orderedProfessionCodesArr = userProfessionCodesArr.sort((a, b) => {
      const indexA = professionCodesOrder.indexOf(a);
      const indexB = professionCodesOrder.indexOf(b);

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    return orderedProfessionCodesArr.map((code) => UserProfession.getName(code));
  }, [user?.professions]);

  const handleInviteNewShift = () => {
    // Legacy tracking for backwards compatibility
    trackEvent('Professional Hub - New Shift Clicked', {
      professionalId: user?.id,
      profession: user?.profession,
    });

    // New PostHog tracking with single event and properties
    const posthog = PosthogService.getInstance();

    posthog.track(POSTHOG_EVENTS.OFFICE_INVITE_SENT, {
      source: source || 'unknown',
      invite_type: 'new_shift',
      location_category: getInviteLocationCategory(source),
      professional_id: user?.id,
      profession: user?.profession,
      office_id: user?.officeId,
    });

    history.push('/dashboard/multi-day/info', {
      professionals: [user?.id],
      professionTypesNames: getOrderedProfessionNames(),
      officeId: user?.officeId,
    });
  };

  useEffect(() => {
    if (isSuccessRequest && professionalId === user?.id) {
      dispatch(clearSuccess(actions));
      if (offers?.length) {
        history.push(`/invite-professional`, {
          professional: {
            id: user?.id,
            image: user?.image,
            name: user?.name,
            profession: user?.profession,
            isFavorite: user?.isFavorite,
            isRecommendedPros: user?.isRecommendedPros,
            isRecent: user?.isRecent,
          },
        });
      } else {
        dispatch(
          showMessage({
            title: 'No Upcoming Shifts',
            message: (
              <div
                style={{
                  textAlign: 'left',
                }}
              >
                Looks like there are no shifts that can be sent to this professional. This may
                happen because either:
                <br />
                <br />
                <ul>
                  <li>Your office does not have any shifts posted.</li>
                  <li>
                    This professional is unavailable or already booked for the dates you need
                    coverage.
                  </li>
                </ul>
              </div>
            ),
            popupProps: {
              rightButtonText: 'Close',
              rightButtonAction: () => {
                dispatch(clearSuccess(actions));
              },
              descriptionStyle: {
                marginBottom: 60,
                width: 420,
              },
            },
          }),
        );
      }
    }
  }, [isSuccessRequest, professionalId, dispatch, user, offers]);

  return (
    <DotsMenu
      dataCy="invite-menu-button"
      position={position}
      disabled={disabled}
      onValidate={onValidate}
      CustomMenuIcon={renderCustomMenuIcon}
      listener={() => listener?.()}
    >
      <MenuItem
        dataCy="existing-shift"
        text="Existing Shift"
        onClick={handleInviteToExistingShift}
      />
      <MenuItem dataCy="new-shift" text="New Shift" onClick={handleInviteNewShift} />
    </DotsMenu>
  );
};

const styles = {
  buttonStyle: {
    display: 'flex',
    height: 50,
    width: 50,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    backgroundColor: Colors.primary_500,
  },
  imgStyle: {
    width: 24,
    height: 24,
  },
};
